/**
 * Popup MFEs — apps that mount in an overlay ABOVE the main routed content
 * instead of replacing the page. Ported from the legacy shell's
 * `popup-mfe-constants.ts` (master `raptor-shell-ui`).
 *
 * The Angular shell renders these through a named `popup` router-outlet
 * (`(popup:apps/<id>)`); the React shell has no named outlets, so it renders
 * them in `PopupMfeOverlay`, driven by `popup-store`. The set of ids and the
 * header titles are kept in lockstep with the legacy constants so the same
 * apps (chatbot `mcpchatbot`, support ticket `stf`, orchestrator, generic
 * `mfepopup`) behave identically.
 *
 * The shell adds NO chrome (header, close button, width constraint) to popup
 * MFEs. Each MFE owns its own modal dialog, backdrop, sizing, and close
 * button, matching Angular's `modal-popup` which just renders
 * `<app-mfe-container [isPopup]="true">` with no wrapper.
 */
export const POPUP_MFE_IDS: readonly string[] = [
  'mfepopup',
  'stf',
  'orchestratoruitasks',
  'mcpchatbot',
];

/** Popup MFEs whose load is intentionally deferred (legacy `Delay_POPUP_MFE_IDS`). */
export const DELAY_POPUP_MFE_IDS: readonly string[] = ['orchestratoruitasks'];

/**
 * Popup MFEs that close when the shell routes the main content to another MFE.
 * Angular's named `popup` outlet is dropped whenever `router.navigateByUrl`
 * targets a new app URL, so the Orchestrator window closed the moment one of its
 * task actions (e.g. "Launch K1 Review") launched the target MFE in the
 * background. Popups absent from this list (e.g. `mcpchatbot`) intentionally
 * survive a primary navigation because the MFE sets its own context on load; see
 * `navigateBrowserToLegacyTarget`.
 */
export const POPUP_MFE_CLOSE_ON_NAVIGATION_IDS: readonly string[] = ['orchestratoruitasks'];

/** Aria-label for the popup dialog per appId (falls back to the id itself). */
export const POPUP_MFE_HEADER_TEXT: Record<string, string> = {
  stf: 'Create a Support Ticket',
  orchestratoruitasks: 'Orchestrator',
  mcpchatbot: 'EYQ FS',
};

export interface PopupMfeLayout {
  variant: 'default' | 'wide';
}

const DEFAULT_POPUP_MFE_LAYOUT: PopupMfeLayout = { variant: 'default' };

const POPUP_MFE_LAYOUTS: Record<string, PopupMfeLayout> = {
  orchestratoruitasks: { variant: 'wide' },
};

/** True when `appId` mounts as a popup MFE (case-insensitive). */
export function isPopupMfe(appId: string): boolean {
  return POPUP_MFE_IDS.includes(appId.toLowerCase());
}

/** True when `appId`'s popup overlay must close on a primary (page) navigation. */
export function closesPopupOnNavigation(appId: string): boolean {
  return POPUP_MFE_CLOSE_ON_NAVIGATION_IDS.includes(appId.toLowerCase());
}

/** Aria-label for the popup overlay per appId (falls back to the id itself). */
export function popupMfeHeaderText(appId: string): string {
  return POPUP_MFE_HEADER_TEXT[appId.toLowerCase()] ?? appId;
}

export function getPopupMfeLayout(appId: string): PopupMfeLayout {
  return POPUP_MFE_LAYOUTS[appId.toLowerCase()] ?? DEFAULT_POPUP_MFE_LAYOUT;
}

export function popupMfePanelClassName(appId: string): string {
  const normalized = appId.toLowerCase();
  const layout = getPopupMfeLayout(normalized);
  return [
    'popup-mfe-panel',
    'motif-theme-light',
    `popup-mfe-panel--${normalized}`,
    `popup-mfe-panel--${layout.variant}`,
  ].join(' ');
}
